import { faCheck, faFire, faFolder } from "@fortawesome/free-solid-svg-icons";
import {
    faBullhorn,
    faComment,
    faCube,
    faDash,
    faMessage,
    faMinus,
    faPlus,
    faSquareQuestion,
    faTimes,
    faWarning,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { StringID } from "userful-chronos-app-common-js/dist/models/common";
import { CompGroup } from "userful-chronos-app-common-js/dist/models/group/Groups";
import { SamSourceAsset } from "userful-chronos-app-common-js/dist/models/sam/SAMAsset";
import { SamSourceDef } from "userful-chronos-app-common-js/dist/models/sam/SAMSource";
import { ITable, ITableRow } from "userful-chronos-app-common-js/dist/models/table/userfulTableInterface";
import { UConductWorkspaceComp } from "userful-chronos-app-common-js/dist/models/uconduct/View";
import { AlertConfig } from "userful-chronos-common-store/dist/Store/notifications/models/alertInterfaces";
import { AppsConfig } from "../SAM/formUtils";
import UserfulTooltip from "../Widgets/UserfulTooltip";
import AlertMenu from "./sources/AlertMenu";
import UConductWorkspaceMenu from "./sources/UConductWorkspaceMenu";
import { IUniversalItem } from "userful-chronos-app-common-js/dist/itemsControl/itemsControl";
import SourceActionsMenu from "../dropdowns/SourceActionsMenu";
import FolderActionsMenu from "../dropdowns/FolderActionsMenu";

export function capitalizeFirstLetter(string: string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
}

export function breakWordsOnCaseChange(string: string) {
    return string.replace(/([A-Z])/g, " $1").trim();
}

export function prettifyString(string: string, t) {
    let word = t("Prettify." + string.toLowerCase());

    if (word && word.length > 0) {
        return word;
    }

    const brokenString = breakWordsOnCaseChange(string);
    return capitalizeFirstLetter(brokenString);
}

export function prettifyAppIDs(appIDs: StringID[]) {
    return (
        <>
            {appIDs.length > 0 &&
                appIDs.map((appID, index) => {
                    const app = AppsConfig.find((a) => a.id === appID.value);

                    if (app)
                        return (
                            <UserfulTooltip key={index} title={app.name}>
                                <div key={app.name} className="userful-table-image-container">
                                    {app.titleIcon}
                                </div>
                            </UserfulTooltip>
                        );
                })}
            {appIDs.length === 0 && <FontAwesomeIcon icon={faMinus}></FontAwesomeIcon>}
        </>
    );
}

export function prettifyTags(tags: StringID[]) {
    return (
        <>
            {tags.map((m, index) => {
                return (
                    <div key={m.value} className="tagNameObject tagNameObjectNonInteractive">
                        {m.value}
                    </div>
                );
            })}
        </>
    );
}

function selectOdd(all: IUniversalItem[]): string[] {
    return all.filter((_, index) => index % 2 === 1).map((item) => item.item.id.value);
}

function selectEven(all: IUniversalItem[]): string[] {
    return all.filter((_, index) => index % 2 === 0).map((item) => item.item.id.value);
}

export const convertSourcesToTable = (
    t,
    objects: IUniversalItem[],
    onMoveOut: (rows: IUniversalItem[]) => void,
    onDelete: (rows: StringID, recursive: boolean) => void,
    onEdit: (row: IUniversalItem) => void,
    parent?: CompGroup,
): ITable => {
    const table: ITable = { id: "Sources", headers: [], rows: [], org: { singular: "Row", plural: "Rows" } };

    table.headers = [
        { id: "name", component: <>{t("Shared.name")}</> },
        { id: "items", component: <>{t("Shared.items")}</> },
        { id: "description", component: <>{t("Shared.description")}</>, hidden: true },
        { id: "type", component: <>{t("Shared.type")}</> },
        { id: "resolution", component: <>{t("Shared.resolution")}</> },
        { id: "tags", component: <>{t("Shared.tags")}</> },
        { id: "scalling", component: <>{t("Shared.scalling")}</> },
        { id: "appPermissions", component: <>{t("Shared.appPermissions")}</> },
        { id: "sourceSharing", component: <>{t("Shared.sourceSharing")}</> },
    ];

    table.defaultSortBy = { id: "name", direction: "asc" };

    table.multiselect = [];

    const folders = objects.filter((object) => object.type === "folder").map((object) => object.item as CompGroup);

    for (let i = 0; i < objects.length; i++) {
        const object = objects[i];

        const row: ITableRow = {
            id: object.item.id.value,

            columns: [
                {
                    id: "name",
                    component: (
                        <div className="column-container-l">
                            <FontAwesomeIcon
                                className={`${object.type === "folder" ? "yellow-icon" : "blue-icon"}`}
                                icon={object.type === "folder" ? faFolder : faCube}
                            />{" "}
                            <p>{object.item.name}</p>
                        </div>
                    ),
                    sortBy: object.item.name,
                },
                {
                    id: "items",
                    component:
                        object.type === "source" ? (
                            <FontAwesomeIcon icon={faMinus}></FontAwesomeIcon>
                        ) : (
                            <p>{(object.item as CompGroup).comps.length}</p>
                        ),
                },
                {
                    id: "description",
                    component: <p>{object.item.description}</p>,
                },
                {
                    id: "type",
                    component: (
                        <p>
                            {object.type === "source" ? (
                                prettifyString((object.item as SamSourceDef).type.value, t)
                            ) : (
                                <FontAwesomeIcon icon={faMinus}></FontAwesomeIcon>
                            )}
                        </p>
                    ),
                },
                {
                    id: "resolution",
                    component:
                        object.type === "source" ? (
                            <p>
                                {(object.item as SamSourceDef).displayResolution.width} x{" "}
                                {(object.item as SamSourceDef).displayResolution.height}
                            </p>
                        ) : (
                            <FontAwesomeIcon icon={faMinus}></FontAwesomeIcon>
                        ),
                    sortBy:
                        object.type === "source"
                            ? (object.item as SamSourceDef).displayResolution.width /
                              (object.item as SamSourceDef).displayResolution.height
                            : "",
                },
                {
                    id: "tags",
                    component: (
                        <div className="column-container-l">
                            {object.type === "source" ? (
                                (object.item as SamSourceDef).orgData.tagsSet.tags.length > 0 ? (
                                    prettifyTags((object.item as SamSourceDef).orgData.tagsSet.tags)
                                ) : (
                                    <FontAwesomeIcon icon={faMinus}></FontAwesomeIcon>
                                )
                            ) : (object.item as CompGroup).tagsSet.tags.length > 0 ? (
                                prettifyTags((object.item as CompGroup).tagsSet.tags)
                            ) : (
                                <FontAwesomeIcon icon={faMinus}></FontAwesomeIcon>
                            )}
                        </div>
                    ),
                    sortBy:
                        object.type === "source"
                            ? (object.item as SamSourceDef).orgData.tagsSet.tags.length
                            : (object.item as CompGroup).tagsSet.tags.length,
                    tooltip: (
                        <>
                            {object.type === "source" ? (
                                (object.item as SamSourceDef).orgData.tagsSet.tags.length > 0 ? (
                                    prettifyTags((object.item as SamSourceDef).orgData.tagsSet.tags)
                                ) : (
                                    <FontAwesomeIcon icon={faMinus}></FontAwesomeIcon>
                                )
                            ) : (object.item as CompGroup).tagsSet.tags.length > 0 ? (
                                prettifyTags((object.item as CompGroup).tagsSet.tags)
                            ) : (
                                <FontAwesomeIcon icon={faMinus}></FontAwesomeIcon>
                            )}
                        </>
                    ),
                },
                {
                    id: "scalling",
                    component: (
                        <p>
                            {object.type === "source" ? (
                                prettifyString((object.item as SamSourceDef).scaling, t)
                            ) : (
                                <FontAwesomeIcon icon={faMinus}></FontAwesomeIcon>
                            )}
                        </p>
                    ),
                },
                {
                    id: "appPermissions",
                    component: (
                        <div className="column-container">
                            {object.type === "source"
                                ? prettifyAppIDs(object.item.orgData.appIDs.appIDs)
                                : prettifyAppIDs(object.item.appIDs.appIDs)}
                        </div>
                    ),
                    sortBy:
                        object.type === "source"
                            ? object.item.orgData.appIDs.appIDs.length
                            : object.item.appIDs.appIDs.length,
                    hideTooltip: true,
                },
                {
                    id: "sourceSharing",
                    component: (
                        <p>
                            {object.type === "source" ? (
                                prettifyString((object.item as SamSourceDef).sharingMode, t)
                            ) : (
                                <FontAwesomeIcon icon={faMinus}></FontAwesomeIcon>
                            )}
                        </p>
                    ),
                },
            ],
            actionsComponents:
                object.type === "source"
                    ? [
                          <SourceActionsMenu
                              value={object.item as SamSourceDef}
                              onMoveOut={() => onMoveOut([object])}
                              onEdit={() => {
                                  onEdit(object);
                              }}
                          ></SourceActionsMenu>,
                      ]
                    : [
                          <FolderActionsMenu
                              parent={parent}
                              value={object.item as CompGroup}
                              comps={folders}
                              onDelete={(id, recursive) => onDelete(object.item.id, recursive)}
                              onMoveOut={() => onMoveOut([object])}
                              onEdit={() => {
                                  onEdit(object);
                              }}
                          />,
                      ],
            isDraggable: true,
            isDroppable: object.type === "folder",
        };
        table.rows.push(row);
    }

    return table;
};

export const convertDestinationOptionsToTable = (
    t,
    destinationOptions: { value: string; label: string; type: string }[],
    selectedDestinations: {
        value: string;
        label: string;
        type: string;
    }[],
): ITable => {
    const table: ITable = { id: "Destinations", headers: [], rows: [] };

    table.headers = [
        { id: "name", component: <>{t("Shared.name")}</>, width: "calc(100% - 40px)", minWidth: 76 },
        { id: "selected", component: "", width: 40, minWidth: 40 },
    ];

    for (let i = 0; i < destinationOptions.length; i++) {
        const destinationOption = destinationOptions[i];
        const foundInSelected = selectedDestinations.find((d) => d.value === destinationOption.value);

        const row: ITableRow = {
            id: destinationOption.value,
            columns: [
                {
                    id: "name",
                    component: <p className="limit">{destinationOption.label}</p>,
                },
                {
                    id: "selected",
                    component: (
                        <>
                            {foundInSelected && <FontAwesomeIcon className="green-icon" icon={faCheck} />}
                            {!foundInSelected && <FontAwesomeIcon className="def-icon" icon={faPlus} />}
                        </>
                    ),
                },
            ],
            actionsComponents: [<></>],
            isChecked: Boolean(foundInSelected),
        };
        table.rows.push(row);
    }

    return table;
};

export const convertCanvasesOptionsToTable = (
    t,
    destinationOptions: { value: string; label: string }[],
    selectedDestinations: {
        value: string;
        label: string;
    }[],
): ITable => {
    const table: ITable = { id: "Canvases", headers: [], rows: [] };

    table.headers = [
        { id: "name", component: <>{t("Shared.name")}</>, width: "calc(100% - 40px)", minWidth: 76 },
        { id: "selected", component: "", width: 40, minWidth: 40 },
    ];

    for (let i = 0; i < destinationOptions.length; i++) {
        const destinationOption = destinationOptions[i];
        const foundInSelected = selectedDestinations.find((d) => d.value === destinationOption.value);

        const row: ITableRow = {
            id: destinationOption.value,
            columns: [
                {
                    id: "name",
                    component: <p className="limit">{destinationOption.label}</p>,
                },
                {
                    id: "selected",
                    component: (
                        <>
                            {foundInSelected && <FontAwesomeIcon className="green-icon" icon={faCheck} />}
                            {!foundInSelected && <FontAwesomeIcon className="def-icon" icon={faPlus} />}
                        </>
                    ),
                },
            ],
            actionsComponents: [<></>],
            isChecked: Boolean(foundInSelected),
        };
        table.rows.push(row);
    }

    return table;
};

export const convertUConductWorkspacesToTable = (t, objects: UConductWorkspaceComp[]): ITable => {
    const table: ITable = { id: "UConductWorkspaces", headers: [], rows: [], org: { singular: "Row", plural: "Rows" } };

    table.headers = [
        { id: "name", component: <>{t("Shared.name")}</> },
        { id: "description", component: <>{t("Shared.description")}</>, hidden: true },
        { id: "interactivViwer", component: <>{t("Shared.interactivViwer")}</> },
    ];

    table.defaultSortBy = { id: "name", direction: "asc" };

    for (let i = 0; i < objects.length; i++) {
        const object = objects[i];

        const row: ITableRow = {
            id: object.id.value,
            columns: [
                {
                    id: "name",
                    component: (
                        <div className="column-container-l">
                            <img src="./assets/uConduct.svg" alt="uConduct" />
                            <p>{object.name}</p>
                        </div>
                    ),
                    sortBy: object.name,
                },
                {
                    id: "description",
                    component: <p>{object.description}</p>,
                },
                {
                    id: "interactivViwer",
                    component: <p>{object.containerIDs.length}</p>,
                },
            ],
            actionsComponents: [<UConductWorkspaceMenu value={object} />],
            isDraggable: false,
            isDroppable: false,
        };
        table.rows.push(row);
    }

    return table;
};

export const convertAetherNotificationsToTable = (
    t,
    objects: AlertConfig[],
    assets: SamSourceAsset[],
    onEdit: (notification: AlertConfig) => void,
    onActivation: (notification: AlertConfig) => void,
    onSchedule: (notification: AlertConfig) => void,
    onDelete: (notification: AlertConfig) => void,
): ITable => {
    const table: ITable = { id: "Notifications", headers: [], rows: [], org: { singular: "Alert", plural: "Alerts" } };

    table.headers = [
        { id: "name", component: <>{t("Shared.name")}</> },
        { id: "description", component: <>{t("Shared.description")}</>, hidden: true },
        { id: "severity", component: <>{t("Shared.severity")}</> },
        { id: "state", component: <>{t("Shared.state")}</> },
        { id: "message", component: <>{t("Shared.message")}</> },
        { id: "assets", component: <>{t("Shared.assets")}</> },
    ];

    table.defaultSortBy = { id: "name", direction: "asc" };

    for (let i = 0; i < objects.length; i++) {
        const object = objects[i];

        const foundAsset = assets.find((asset) => asset.id.value === object.assets[0]?.value);

        let alertSeverity: { icon: any; color: string; str: string } = {
            icon: faSquareQuestion,
            color: "grey",
            str: "Unknown",
        };
        if (object.alertSeverity === "ANNOUNCEMENT") {
            alertSeverity.icon = faComment;
            alertSeverity.color = "green";
            alertSeverity.str = "Notifications.Types.announcement";
        } else if (object.alertSeverity === "CAUTION_RISK_DRILL") {
            alertSeverity.icon = faWarning;
            alertSeverity.color = "yellow";
            alertSeverity.str = "Notifications.Types.caution";
        } else if (object.alertSeverity === "REAL_EMERGENCY") {
            alertSeverity.icon = faFire;
            alertSeverity.color = "red";
            alertSeverity.str = "Notifications.Types.emergency";
        }

        let state: { icon: any; color: string; str: string } = {
            icon: faSquareQuestion,
            color: "grey",
            str: "Unknown",
        };
        if (object.active) {
            state.icon = faCheck;
            state.color = "green";
            state.str = "Notifications.Table.active";
        } else {
            state.icon = faTimes;
            state.color = "red";
            state.str = "Notifications.Table.inactive";
        }

        let message: { icon: any; color: string; str: string } = {
            icon: faSquareQuestion,
            color: "grey",
            str: "Unknown",
        };
        if (object.textOverlay.text.length > 0) {
            message.icon = faMessage;
            message.color = "grey";
            message.str = object.textOverlay.text;
        } else {
            message.icon = faTimes;
            message.color = "red";
            message.str = "";
        }

        let image: { icon: any; color: string; str: string } = {
            icon: faSquareQuestion,
            color: "grey",
            str: "Unknown",
        };
        if (object.assets.length > 0 && foundAsset) {
            image.icon = faCheck;
            image.color = "green";
            image.str = foundAsset.name;
        } else {
            image.icon = faTimes;
            image.color = "red";
            image.str = "";
        }

        const row: ITableRow = {
            id: object.id.value,
            columns: [
                {
                    id: "name",
                    component: (
                        <div className="column-container-l">
                            <FontAwesomeIcon className="grey-icon" icon={faBullhorn} />
                            <p>{object.name}</p>
                        </div>
                    ),
                    sortBy: object.name,
                },
                {
                    id: "description",
                    component: object.description ? <p>{object.description}</p> : <FontAwesomeIcon icon={faDash} />,
                },
                {
                    id: "severity",
                    component: (
                        <div className="column-container-l">
                            <FontAwesomeIcon className={`${alertSeverity.color}-icon`} icon={alertSeverity.icon} />
                            <p>{t(alertSeverity.str)}</p>
                        </div>
                    ),
                    sortBy: alertSeverity.str,
                },
                {
                    id: "state",
                    component: (
                        <div className="column-container-l">
                            <FontAwesomeIcon className={`${state.color}-icon`} icon={state.icon} />
                            <p>{t(state.str)}</p>
                        </div>
                    ),
                    sortBy: state.str,
                },
                {
                    id: "message",
                    component: message.str ? <p>{t(message.str)}</p> : <FontAwesomeIcon icon={faDash} />,
                    sortBy: message.str,
                    tooltip: message.str,
                },
                {
                    id: "assets",
                    component: image.str ? <p>{t(image.str)}</p> : <FontAwesomeIcon icon={faDash} />,
                    sortBy: image.str,
                    tooltip: image.str,
                },
            ],
            actionsComponents: [
                <AlertMenu
                    value={object}
                    onEdit={onEdit}
                    onActivation={onActivation}
                    onSchedule={onSchedule}
                    onDelete={onDelete}
                />,
            ],
            isDraggable: false,
            isDroppable: false,
        };
        table.rows.push(row);
    }

    return table;
};
